"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import { cn } from "@repo/common/utils/cn";
import { Input } from "@repo/ui/components/ui/input";

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        "group/input-group border-input shadow-xs relative flex w-full items-center rounded-md border outline-none transition-[color,box-shadow] ring-offset-background",
        "h-9 has-[>textarea]:h-auto",
        "overflow-hidden",
        // Storybook pseudo state support - :has() can't contain pseudo-classes like :focus-visible
        "has-[[data-slot=input-group-select-trigger].pseudo-focus-visible]:overflow-visible",
        "has-[[data-slot=input-group-segment].pseudo-focus-visible]:overflow-visible",

        // Variants based on alignment.
        "has-[>[data-align=inline-start]]:[&>input]:pl-2",
        "has-[>[data-align=inline-end]]:[&>input]:pr-2",
        "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3",
        "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3",

        // Focus state.
        // Storybook pseudo state support - :has() can't contain pseudo-classes like :focus-visible
        "has-[[data-slot=input-group-control].pseudo-focus-visible]:ring-2 has-[[data-slot=input-group-control].pseudo-focus-visible]:ring-action-primary-hover has-[[data-slot=input-group-control].pseudo-focus-visible]:ring-offset-2",

        // Disabled state.
        "has-[[data-slot=input-group-control]:disabled]:bg-background-muted has-[[data-slot=input-group-control]:disabled]:cursor-not-allowed has-[[data-slot=input-group-control]:disabled:placeholder-shown]:bg-background has-[[data-slot=input-group-control]:disabled:placeholder-shown]:opacity-50",

        // Error state.
        "has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive",

        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  "text-foreground/60 relative z-10 flex h-auto cursor-text select-none items-center justify-center gap-2 py-1.5 text-sm font-medium group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4 has-[>[data-slot=input-group-icon-button]]:py-0 has-[>[data-slot=input-group-icon-button]]:h-full",
  {
    variants: {
      align: {
        "inline-start": [
          "order-first",
          "pl-3",
          // Button spacing
          "has-[>[data-slot=input-group-button]]:ml-[-0.45rem]",
          "has-[>kbd]:ml-[-0.35rem]",
          // Segment styling
          "has-[>[data-slot=input-group-segment]]:h-full",
          "has-[>[data-slot=input-group-segment]]:p-0",
          "[&>[data-slot=input-group-segment]]:border-r",
          "[&>[data-slot=input-group-segment]]:border-border",
          "[&>[data-slot=input-group-segment]:focus-visible]:rounded-l-md",
          "[&>[data-slot=input-group-segment].pseudo-focus-visible]:rounded-l-md",
          "has-[>[data-slot=input-group-segment]]:self-stretch",
          // Select trigger styling
          "has-[>[data-slot=input-group-select-trigger]]:h-full",
          "has-[>[data-slot=input-group-select-trigger]]:p-0",
          "[&>[data-slot=input-group-select-trigger]]:border-r",
          "[&>[data-slot=input-group-select-trigger]]:border-border",
          "[&>[data-slot=input-group-select-trigger]:focus-visible]:rounded-l-md",
          "[&>[data-slot=input-group-select-trigger].pseudo-focus-visible]:rounded-l-md",
          "has-[>[data-slot=input-group-select-trigger]]:self-stretch",
        ],
        "inline-end": [
          "order-last",
          "pr-3",
          // Button spacing
          "has-[>[data-slot=input-group-button]]:mr-[-0.4rem]",
          "has-[>[data-slot=input-group-icon-button]]:mr-[-0.5rem]",
          "has-[>kbd]:mr-[-0.35rem]",
          // Segment styling
          "has-[>[data-slot=input-group-segment]]:h-full",
          "has-[>[data-slot=input-group-segment]]:p-0",
          "[&>[data-slot=input-group-segment]]:border-l",
          "[&>[data-slot=input-group-segment]]:border-border",
          "[&>[data-slot=input-group-segment]:focus-visible]:rounded-r-md",
          "[&>[data-slot=input-group-segment].pseudo-focus-visible]:rounded-r-md",
          "has-[>[data-slot=input-group-segment]]:self-stretch",
          // Select trigger styling
          "has-[>[data-slot=input-group-select-trigger]]:h-full",
          "has-[>[data-slot=input-group-select-trigger]]:p-0",
          "[&>[data-slot=input-group-select-trigger]]:border-l",
          "[&>[data-slot=input-group-select-trigger]]:border-border",
          "[&>[data-slot=input-group-select-trigger]:focus-visible]:rounded-r-md",
          "[&>[data-slot=input-group-select-trigger].pseudo-focus-visible]:rounded-r-md",
          "has-[>[data-slot=input-group-select-trigger]]:self-stretch",
        ],
        "block-start": [
          "[.border-b]:pb-3",
          "order-first",
          "w-full",
          "justify-start",
          "px-3",
          "pt-3",
          "group-has-[>input]/input-group:pt-2.5",
        ],
        "block-end": [
          "[.border-t]:pt-3",
          "order-last",
          "w-full",
          "justify-start",
          "px-3",
          "pb-3",
          "group-has-[>input]/input-group:pb-2.5",
        ],
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  },
);

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button")) {
          return;
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus();
      }}
      {...props}
    />
  );
}

const InputGroupButton = ({
  className,
  type = "button",
  ...props
}: React.ComponentProps<"button">) => {
  return (
    <button
      type={type}
      className={cn(
        "flex h-full items-center gap-2 bg-action-neutral-inverse px-3 text-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
        "cursor-pointer hover:bg-action-neutral-inverse-hover",
        "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-primary-hover focus-visible:ring-offset-2",
        className,
      )}
      {...props}
      data-slot="input-group-segment"
    />
  );
};
InputGroupButton.displayName = "InputGroupButton";

function InputGroupIconButton({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-group-icon-button"
      className={cn("", className)}
      {...props}
    />
  );
}

function InputGroupSegment({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "flex h-full items-center gap-2 bg-action-neutral-inverse px-3 text-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
        className,
      )}
      {...props}
      data-slot="input-group-segment"
    />
  );
}

function InputGroupSelectTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<"button">) {
  return (
    <button
      type="button"
      data-slot="input-group-select-trigger"
      className={cn(
        "flex h-full w-full cursor-pointer items-center gap-2 bg-action-neutral-inverse px-3 text-sm outline-none transition-colors",
        "hover:bg-action-neutral-inverse-hover",
        "focus-visible:ring-2 focus-visible:ring-action-primary-hover focus-visible:ring-offset-[3px] focus-visible:ring-offset-background",
        "[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDown className="ml-auto size-4 shrink-0 opacity-50" />
    </button>
  );
}

const InputGroupInput = ({
  className,
  ...props
}: React.ComponentProps<"input"> & {
  passwordrules?: string; // for password managers
  autocomplete?: string; // for password managers
  sizeVariant?: "md" | "lg";
}) => {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        [
          "flex-1",
          "focus-visible:ring-0 focus-visible:ring-offset-0 ring-offset-0",
          "rounded-none outline-none border-0 shadow-none bg-transparent disabled:bg-transparent",
        ],
        className,
      )}
      {...props}
    />
  );
};
InputGroupInput.displayName = "InputGroupInput";

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupIconButton,
  InputGroupSegment,
  InputGroupSelectTrigger,
  InputGroupInput,
};
